import { env } from '@/env';
import { useAsync } from '@/hooks/use-async';
import { useAsyncAction } from '@/hooks/use-async-action';
import { HostedPciTokens } from '@/types/hostedpci';
import { promiseWithResolvers } from '@/util/promise-with-resolvers';
import { useEffect, useRef, useState } from 'react';

interface HostedPciConfig {
  hostedPciLocation: string;
  hostedPciSiteId: string;
  hostedPciReportCCType: string;
  hostedPciReportCCDigits: string;
  hostedPciReportCVVDigits: string;
}

interface HostedPciIframeProps {
  config: HostedPciConfig;
  onTokensReceived?: (tokens: HostedPciTokens) => void;
  onError?: (error: string) => void;
  className?: string;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if script already exists
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.type = 'text/javascript';
    script.charset = 'utf-8';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.body.appendChild(script);
  });
}

const iFrameName = 'ccframe';
const iFrameFormId = 'CCAcceptForm';

export function HostedPciIframe({
  config,
  onTokensReceived,
  onError,
  className,
}: HostedPciIframeProps) {
  const [iframeUrl, setIframeUrl] = useState<string>('');

  // Build iframe URL and set global variables required by HostedPCI
  useEffect(() => {
    const baseUrl = new URL(env.VITE_HOSTED_PCI_BASE_URL);

    baseUrl.searchParams.set('locationName', config.hostedPciLocation);
    baseUrl.searchParams.set('sid', config.hostedPciSiteId);
    baseUrl.searchParams.set('reportCCType', config.hostedPciReportCCType);
    baseUrl.searchParams.set('reportCCDigits', config.hostedPciReportCCDigits);
    baseUrl.searchParams.set('reportCVVDigits', config.hostedPciReportCVVDigits);
    baseUrl.searchParams.set('fullParentHost', window.location.origin);
    baseUrl.searchParams.set('fullParentQStr', window.location.pathname);

    const hpciCCFrameFullUrl = baseUrl.toString();

    // Set global variables required by HostedPCI library
    window.hpciCCFrameHost = baseUrl.origin;
    window.hpciCCFrameFullUrl = hpciCCFrameFullUrl;
    window.hpciCCFrameName = iFrameName;

    setIframeUrl(hpciCCFrameFullUrl);
  }, [config]);

  const { data: scriptsLoaded } = useAsync(
    async () => {
      await loadScript('https://code.jquery.com/jquery-1.12.4.min.js');
      await loadScript(
        'https://ccframe.hostedpci.com/WBSStatic/site60/proxy/js/jquery.ba-postmessage.2.0.0.min.js',
      );
      await loadScript(
        'https://ccframe.hostedpci.com/WBSStatic/site60/proxy/js/hpci-cciframe-1.0.js',
      );

      window.hpciSiteSuccessHandlerV3 = (ccNum, ccCVV, ccBIN, gtyToken) => {
        onTokensReceived?.({
          ccNum,
          ccCVV,
          ccBIN,
          gtyToken: gtyToken,
        });
      };

      window.hpciSiteErrorHandler = (_errorCode, errorMsg) => {
        onError?.(errorMsg || 'Credit card processing error');
      };

      return true;
    },
    [],
    () => {
      if (window.hpciSiteSuccessHandlerV3) {
        delete window.hpciSiteSuccessHandlerV3;
      }
      if (window.hpciSiteErrorHandler) {
        delete window.hpciSiteErrorHandler;
      }
    },
  );

  if (!scriptsLoaded) {
    return null;
  }

  return (
    <>
      <iframe
        id={iFrameName}
        name={iFrameName}
        src={iframeUrl}
        onLoad={window.receiveHPCIMsgAfterPageReset}
        width="90%"
        height="62"
        frameBorder="0"
        scrolling="no"
        className={className}
        title="Credit Card Input"
      />
      {/* Hidden input fields for HostedPCI's library to populate */}
      <input id="ccNum" name="ccNum" type="hidden" value=" " />
      <input id="ccCVV" name="ccCVV" type="hidden" value=" " />
      <input id="ccBIN" name="ccBIN" type="hidden" value=" " />
      <input name="action3DSec" type="hidden" value="verifyenroll" />
    </>
  );
}

export function useHostedPciPayment() {
  const tokenizationPromiseRef = useRef<PromiseWithResolvers<HostedPciTokens> | null>(null);

  // Trigger tokenization
  const {
    perform: requestTokenization,
    loading: isProcessing,
    error,
  } = useAsyncAction(async (): Promise<HostedPciTokens> => {
    if (tokenizationPromiseRef.current) {
      return tokenizationPromiseRef.current.promise;
    }

    const tokenizationRequest = promiseWithResolvers<HostedPciTokens>();
    tokenizationPromiseRef.current = tokenizationRequest;

    if (!window.sendHPCIMsg) {
      tokenizationRequest.reject(new Error('Payment processor not initialized'));
    } else {
      // HostedPCI skips sendHPCIMsg when hpciStatus is still "success" from a prior attempt.
      window.hpciPageReset();
      // sendHPCIMsg triggers the tokenization
      // The result comes back via hpciSiteSuccessHandlerV3
      window.sendHPCIMsg();
    }

    return tokenizationRequest.promise.finally(() => {
      tokenizationPromiseRef.current = null;
    });
  });

  const handleTokensReceived = (receivedTokens: HostedPciTokens) => {
    tokenizationPromiseRef.current?.resolve(receivedTokens);
  };

  const handleError = (errorMessage: string) => {
    tokenizationPromiseRef.current?.reject(new Error(errorMessage));
  };

  const reset = () => {
    tokenizationPromiseRef.current = null;
  };

  return {
    error,
    isProcessing,
    requestTokenization,
    handleTokensReceived,
    handleError,
    reset,
    iFrameFormId,
  };
}
